import React, { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import type { MyTeam, ProjectMoveRequest } from "@domain/schemas/workspace";

interface MoveProjectDialogProps {
  sourceTeamId: string;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: (request: ProjectMoveRequest) => void;
}

/**
Source-side half of the move handshake only - submitting creates a pending request, it does not
move the project. The target team's Managers accept or reject it from their own team's page.
*/
export const MoveProjectDialog: React.FC<MoveProjectDialogProps> = ({ sourceTeamId, projectId, projectName, onClose, onSuccess }) => {
  const projectManagementService = useInjection(ProjectManagementService);
  const listTeamsService = useInjection(ListTeamsService);
  const { pushNotification } = useNotifications();
  const [candidateTeams, setCandidateTeams] = useState<MyTeam[]>([]);
  const [targetTeamId, setTargetTeamId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    void (async () => {
      try {
        const myTeams = await listTeamsService.getMyTeamsAsync();
        const teams = myTeams.filter((team) => team.id !== sourceTeamId);
        if (!isCancelled) {
          setCandidateTeams(teams);
          setTargetTeamId(teams[0]?.id ?? "");
        }
      } catch {
        if (!isCancelled) setCandidateTeams([]);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [listTeamsService, sourceTeamId]);

  const canSubmit = targetTeamId.length > 0 && !isSubmitting;

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const request = await projectManagementService.proposeProjectMoveAsync(sourceTeamId, projectId, targetTeamId);
      onSuccess(request);
      onClose();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not propose the move.", "error", 10_000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title={`Move ${projectName}`} onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0 }}>This creates a pending request. The target team&apos;s Managers must accept it before the project actually moves.</p>
        {candidateTeams.length === 0 ? (
          <p style={{ margin: 0, color: "#64748b" }}>No other team is available as a move target.</p>
        ) : (
          <div>
            <label htmlFor="move-project-target-team" style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
              Target team
            </label>
            <DropDownList
              id="move-project-target-team"
              style={{ width: "100%" }}
              data={candidateTeams}
              textField="name"
              dataItemKey="id"
              value={candidateTeams.find((team) => team.id === targetTeamId)}
              onChange={(event) => setTargetTeamId((event.value as MyTeam).id)}
            />
          </div>
        )}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={!canSubmit} onClick={handleSubmit}>
          Propose move
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
